import {
  ADMIN_AUDIT_SUMMARY_UNKNOWN,
  getAdminAuditAction,
  isKnownAdminAuditTargetType,
  resolveAdminAuditSummaryKey,
} from '../../constants/adminAudit.js';
import { ADMIN_DATE_PLACEHOLDER } from './adminUserDisplay.js';

export { ADMIN_DATE_PLACEHOLDER, resolveAdminAuditSummaryKey };

const KNOWN_ROLES = new Set(['super_admin', 'admin', 'support']);

function pickString(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function sanitizeRawToken(value) {
  const raw = pickString(value).slice(0, 128);
  if (!raw) return '';
  return /^[A-Za-z0-9_.:-]+$/.test(raw) ? raw : '';
}

/**
 * @param {unknown} role
 * @param {(key: string) => string} t
 * @returns {string}
 */
export function adminAuditRoleLabel(role, t) {
  if (role == null || pickString(role) === '') return t('admin.audit.role.none');
  const normalized = pickString(role);
  if (KNOWN_ROLES.has(normalized)) {
    return t(`admin.audit.role.${normalized}`);
  }
  return sanitizeRawToken(normalized) || t('admin.audit.role.unknown');
}

/**
 * @param {{ displayName?: unknown, email?: unknown, uid?: unknown, id?: unknown } | null | undefined} person
 */
export function resolveAuditIdentity(person) {
  const displayName = pickString(person?.displayName) || null;
  const email = pickString(person?.email) || null;
  const uid = pickString(person?.uid) || pickString(person?.id) || null;
  const primary = displayName || email || uid || ADMIN_DATE_PLACEHOLDER;
  return { displayName, email, uid, primary, missingAuth: Boolean(uid) && !displayName && !email };
}

/**
 * @param {{ action?: unknown, summaryKey?: unknown }} log
 * @param {(key: string) => string} t
 * @returns {{ label: string, rawAction: string | null }}
 */
export function adminAuditActionLabel(log, t) {
  const key = resolveAdminAuditSummaryKey(log);
  const label = t(key);
  const resolved = label === key ? t(ADMIN_AUDIT_SUMMARY_UNKNOWN) : label;
  const rawAction = pickString(log?.action) || null;
  const known = Boolean(getAdminAuditAction(rawAction));
  return {
    label: resolved,
    rawAction: known || !rawAction ? rawAction : sanitizeRawToken(rawAction) || rawAction,
  };
}

/**
 * @param {{ targetType?: unknown, target?: object }} log
 */
export function resolveAuditTarget(log) {
  const targetType = pickString(log?.targetType) || 'user';
  const target = log?.target && typeof log.target === 'object' ? log.target : {};
  if (targetType === 'user' || isKnownAdminAuditTargetType(targetType)) {
    return {
      kind: 'user',
      targetType,
      identity: resolveAuditIdentity({
        displayName: target.displayName,
        email: target.email,
        uid: target.uid || target.id,
        id: target.id,
      }),
    };
  }
  return {
    kind: 'generic',
    targetType: sanitizeRawToken(targetType) || targetType,
    id: pickString(target.id) || null,
    identity: resolveAuditIdentity(target),
  };
}

/**
 * Structured detail rows from the allowlisted payload only. Never dump JSON.
 * @param {object} log
 * @param {(key: string) => string} t
 * @returns {{ key: string, label: string, value: string }[]}
 */
export function adminAuditDetailFields(log, t) {
  const action = pickString(log?.action);
  const metadata = log?.metadata && typeof log.metadata === 'object' && !Array.isArray(log.metadata)
    ? log.metadata
    : {};
  if (action === 'role_changed') {
    return [
      { key: 'from', label: t('admin.audit.details.from'), value: adminAuditRoleLabel(metadata.from, t) },
      { key: 'to', label: t('admin.audit.details.to'), value: adminAuditRoleLabel(metadata.to, t) },
    ];
  }
  return [];
}

/**
 * Fail-closed: details UI must never surface these fragments.
 * @param {unknown} payload
 * @returns {string[]}
 */
export function findForbiddenAuditUiFragments(payload) {
  const json = JSON.stringify(payload);
  const hits = [];
  const forbidden = [
    'customClaims',
    'customToken',
    'inviteToken',
    'invitationLink',
    'operationId',
    'targetEmail',
  ];
  for (const fragment of forbidden) {
    if (json.includes(fragment)) hits.push(fragment);
  }
  return hits;
}
